import React, { useState, useEffect, useCallback } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Row, Col, Card, Badge, Spinner, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";
import usePermission from "../../../hooks/usePermission";
import {
 getEquipments,
 getEquipmentStats,
 deleteEquipment,
} from "../../../services/equipmentService";

const STATUS_COLORS = {
 Available: "success",
 Deployed: "primary",
 "Under Maintenance": "warning",
 Idle: "secondary",
 Retired: "dark",
 "In Transit": "info",
};

const MAINTENANCE_COLORS = {
 Good: "success",
 "Due Soon": "warning",
 Overdue: "danger",
 "Under Repair": "danger",
 "N/A": "secondary",
};

const EquipmentList = () => {
 const navigate = useNavigate();
 const { hasPermission } = usePermission();
 const canManage = hasPermission(["create:equipment", "edit:equipment", "delete:equipment"]);

 const [equipments, setEquipments] = useState([]);
 const [stats, setStats] = useState(null);
 const [loading, setLoading] = useState(true);
 const [search, setSearch] = usePersistentState("equipment_search", "");
 const [statusFilter, setStatusFilter] = usePersistentState("equipment_status", "");
 const [categoryFilter, setCategoryFilter] = usePersistentState("equipment_cat", "");
 const [page, setPage] = usePersistentState("equipment_page", 1);
 const [pagination, setPagination] = useState({});

 const fetchStats = async () => {
 try {
 const res = await getEquipmentStats();
 setStats(res.data?.data || null);
 } catch (_) {}
 };

 const fetchEquipments = useCallback(async () => {
 setLoading(true);
 try {
 const params = { page, limit: 20 };
 if (search) params.search = search;
 if (statusFilter) params.status = statusFilter;
 if (categoryFilter) params.category = categoryFilter;

 const res = await getEquipments(params);
 setEquipments(res.data?.data || []);
 setPagination(res.data?.pagination || {});
 } catch (err) {
 toast.error("Failed to load equipment");
 } finally {
 setLoading(false);
 }
 }, [page, search, statusFilter, categoryFilter]);

 useEffect(() => {
 fetchEquipments();
 }, [fetchEquipments]);

 useEffect(() => {
 fetchStats();
 }, []);

 const handleDelete = async (id, name) => {
 const confirmed = await swal({
 title: "Delete Equipment?",
 text: `"${name}" and all its deployment logs will be permanently removed.`,
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirmed) return;

 try {
 await deleteEquipment(id);
 toast.success("Equipment deleted");
 fetchEquipments();
 fetchStats();
 } catch (err) {
 toast.error("Failed to delete equipment");
 }
 };

 const handleSearch = (e) => {
 setSearch(e.target.value);
 setPage(1);
 };

 return (
 <>
 <PageTitle activeMenu="Equipment" motherMenu="Equipment" />

 {/* Stats Cards */}
 {stats && (
 <Row className="mb-3">
 {[
 { label: "Total", value: stats.total, color: "primary", icon: "fa-cogs" },
 { label: "Deployed", value: stats.deployed, color: "info", icon: "fa-truck" },
 { label: "Available", value: stats.available, color: "success", icon: "fa-check-circle" },
 { label: "Maintenance", value: stats.underMaintenance, color: "warning", icon: "fa-tools" },
 { label: "Idle", value: stats.idle, color: "secondary", icon: "fa-pause-circle" },
 { label: "Maint. Overdue", value: stats.maintenanceOverdue, color: "danger", icon: "fa-exclamation-triangle" },
 ].map((s) => (
 <Col key={s.label} xs={6} sm={4} md={2} className="mb-3">
 <Card className={`bg-${s.color} text-white shadow-sm`}>
 <Card.Body className="p-3 d-flex justify-content-between align-items-center">
 <div>
 <p className="mb-0" style={{ fontSize: "0.75rem", opacity: 0.85 }}>{s.label}</p>
 <h4 className="mb-0 fw-bold">{s.value}</h4>
 </div>
 <i className={`fas ${s.icon} fa-2x opacity-50`} />
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>
 )}

 <Card className="shadow-sm">
 <Card.Header className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
 <h5 className="mb-0">
 <i className="fas fa-cogs me-2 text-primary" />
 Equipment Register
 </h5>
 <div className="d-flex gap-2 flex-wrap">
 <Form.Control
 type="search"
 placeholder="Search equipment..."
 value={search}
 onChange={handleSearch}
 style={{ width: 200 }}
 size="sm"
 />
 <Form.Select
 size="sm"
 value={statusFilter}
 onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
 style={{ width: 160 }}
 >
 <option value="">All Statuses</option>
 {["Available", "Deployed", "Under Maintenance", "Idle", "Retired", "In Transit"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 <Form.Select
 size="sm"
 value={categoryFilter}
 onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
 style={{ width: 180 }}
 >
 <option value="">All Categories</option>
 {["Heavy Machinery", "Light Machinery", "Vehicles", "Power Tools", "Hand Tools", "Measuring Instruments", "Safety Equipment", "Other"].map((c) => (
 <option key={c} value={c}>{c}</option>
 ))}
 </Form.Select>
 {canManage && (
 <Button variant="primary" size="sm" onClick={() => navigate("../equipment/add")}>
 <i className="fas fa-plus me-1" />Add Equipment
 </Button>
 )}
 <Button variant="outline-warning" size="sm" onClick={() => navigate("../equipment/idle-report")}>
 <i className="fas fa-pause-circle me-1" />Idle Report
 </Button>
 <Button variant="outline-secondary" size="sm" onClick={() => navigate("../equipment/deployment-logs")}>
 <i className="fas fa-history me-1" />Deployment Logs
 </Button>
 </div>
 </Card.Header>

 <Card.Body className="p-0">
 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </div>
 ) : equipments.length === 0 ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-cogs fa-3x mb-3 d-block" />
 No equipment found
 </div>
 ) : (
 <div className="table-responsive">
 <table className="table table-hover mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th>Code</th>
 <th>Name</th>
 <th>Category</th>
 <th>Status</th>
 <th>Project</th>
 <th>Location</th>
 <th>Utilization (hrs)</th>
 <th>Maintenance</th>
 <th>Actions</th>
 </tr>
 </thead>
 <tbody>
 {equipments.map((eq) => (
 <tr key={eq._id}>
 <td>
 <span className="badge bg-light text-dark border fw-semibold">
 {eq.equipmentCode}
 </span>
 </td>
 <td>
 <div className="fw-semibold">{eq.name}</div>
 {eq.make && (
 <small className="text-muted">{eq.make} {eq.model}</small>
 )}
 </td>
 <td>
 <span className="badge bg-light text-dark border">{eq.category}</span>
 </td>
 <td>
 <Badge bg={STATUS_COLORS[eq.status] || "secondary"}>
 {eq.status}
 </Badge>
 </td>
 <td>
 {eq.project ? (
 <span className="text-primary small">
 {eq.project.projectName}
 </span>
 ) : (
 <span className="text-muted small">—</span>
 )}
 </td>
 <td>
 <span className="small">{eq.location || "—"}</span>
 </td>
 <td className="text-center">
 <strong>{eq.totalUtilizationHours || 0}</strong>
 </td>
 <td>
 <Badge bg={MAINTENANCE_COLORS[eq.maintenanceStatus] || "secondary"}>
 {eq.maintenanceStatus}
 </Badge>
 </td>
 <td>
 <div className="d-flex align-items-center gap-1 flex-nowrap">
 <Button
 size="sm"
 variant="outline-primary"
 title="View Details"
 onClick={() => navigate(`../equipment/view/${eq._id}`)}
 >
 <i className="fas fa-eye" />
 </Button>
 {canManage && (
 <>
 <Button
 size="sm"
 variant="outline-secondary"
 title="Edit"
 onClick={() => navigate(`../equipment/edit/${eq._id}`)}
 >
 <i className="fas fa-edit" />
 </Button>
 {eq.status !== "Deployed" && (
 <Button
 size="sm"
 variant="outline-primary"
 title="Deploy"
 onClick={() => navigate(`../equipment/deploy/${eq._id}`)}
 >
 <i className="fas fa-truck" />
 </Button>
 )}
 {eq.status === "Deployed" && (
 <Button
 size="sm"
 variant="outline-success"
 title="Return"
 onClick={() => navigate(`../equipment/return/${eq._id}`)}
 >
 <i className="fas fa-undo" />
 </Button>
 )}
 <Button
 size="sm"
 variant="outline-danger"
 title="Delete"
 onClick={() => handleDelete(eq._id, eq.name)}
 >
 <i className="fas fa-trash-alt" />
 </Button>
 </>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </Card.Body>

 {/* Pagination */}
 {pagination.totalPages > 1 && (
 <Card.Footer className="d-flex justify-content-between align-items-center">
 <small className="text-muted">
 {pagination.totalCount} equipment · Page {pagination.currentPage} of {pagination.totalPages}
 </small>
 <div className="d-flex gap-2">
 <Button
 variant="outline-secondary"
 size="sm"
 disabled={page <= 1}
 onClick={() => setPage((p) => p - 1)}
 >
 Prev
 </Button>
 <Button
 variant="outline-secondary"
 size="sm"
 disabled={page >= pagination.totalPages}
 onClick={() => setPage((p) => p + 1)}
 >
 Next
 </Button>
 </div>
 </Card.Footer>
 )}
 </Card>
 </>
 );
};

export default EquipmentList;
